"use client";

import {NeoTextBox} from "@/components/forms";
import {SearchIcon} from "@/components/shared";
import {
  Button,
  Image,
  Pagination,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Table,
  TableBody,
  TableCell,
  TableColumn,
  TableHeader,
  TableRow
} from "@nextui-org/react";
import {useEffect, useRef, useState} from "react";
import {FaArrowDownLong, FaArrowUpLong} from "react-icons/fa6";

type HeaderFilterOption = {
  label: string;
  value: string;
};

type HeaderFilter = {
  key: string;
  options: HeaderFilterOption[];
  value: string[];
  placeholder: string;
  onChange: (selectedOptions: HeaderFilterOption[]) => void;
};

export function ViewTable({
  tabledatas,
  onSelectionRow,
  headerFilter
}: {
  tabledatas: any;
  onSelectionRow: (selectedRows: any[]) => void;
  headerFilter?: HeaderFilter;
}) {
  const [tableData, setTableData] = useState<any[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [headerFilterQuery, setHeaderFilterQuery] = useState("");
  const [isHeaderFilterOpen, setIsHeaderFilterOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [sortKey, setSortKey] = useState<string | null>(null);
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set());
  const headerFilterSearchRef = useRef<HTMLInputElement | null>(null);

  if (tabledatas.length === 0) {
    tabledatas = {
      tableheaders: [],
      tablerows: [],
      checkboxcheck: false
    };
  }

  useEffect(() => {
    setSelectedKeys(new Set());
    if (tabledatas?.tablerows) {
      setTableData(tabledatas.tablerows);
    }
  }, [tabledatas]);

  let filteredRows = Array.isArray(tabledatas?.tablerows)
    ? tabledatas.tablerows
    : [];
  if (searchQuery.trim()) {
    const q = searchQuery.toLowerCase();
    filteredRows = filteredRows.filter((row: any) =>
      Object.values(row).some(val => val?.toString().toLowerCase().includes(q))
    );
  }

  const sortedRows = [...filteredRows];
  if (sortKey) {
    sortedRows.sort((a, b) => {
      const valA = (a[sortKey] ?? "").toString().toLowerCase();
      const valB = (b[sortKey] ?? "").toString().toLowerCase();
      if (valA < valB) return sortOrder === "asc" ? -1 : 1;
      if (valA > valB) return sortOrder === "asc" ? 1 : -1;
      return 0;
    });
  }

  const totalRows = sortedRows.length;
  const totalPages = Math.ceil(totalRows / rowsPerPage);
  const startIndex = (page - 1) * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const currentRows = sortedRows.slice(startIndex, endIndex);

  const handleSort = (key: string) => {
    if (sortKey === key) {
      setSortOrder(prev => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortOrder("asc");
    }
  };

  const onSearchChange = (value: string) => {
    setSearchQuery(value);
    setPage(1);
  };

  const onRowsPerPageChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setRowsPerPage(Number(e.target.value));
    setPage(1);
  };

  useEffect(() => {
    if (Array.isArray(tableData)) {
      const selected = tableData.filter((row: any) =>
        selectedKeys.has(row.userId)
      );
      onSelectionRow(selected);
    }
  }, [selectedKeys, tableData]);

  const handleSelectionChange = (keys: any) => {
    const allRowIds = tableData.map(row => row.userId);
    if (keys === "all") {
      setSelectedKeys(new Set(allRowIds));
    } else {
      setSelectedKeys(new Set(keys));
    }
  };

  const activeHeaderFilter =
    headerFilter &&
    tabledatas?.tableheaders?.some(
      (header: any) => header.key === headerFilter.key
    )
      ? headerFilter
      : null;

  const filteredHeaderFilterOptions = activeHeaderFilter
    ? activeHeaderFilter.options.filter(option =>
        option.label.toLowerCase().includes(headerFilterQuery.toLowerCase())
      )
    : [];

  return (
    <div className="flex w-full max-w-screen-xl flex-col gap-2">
      <div className="flex justify-between">
        <div className="mt-1 flex items-center justify-center gap-2 sm:max-w-[40%]">
          <NeoTextBox
            isClearable
            className="w-full"
            placeholder="Search..."
            startContent={<SearchIcon />}
            size="sm"
            onValueChange={onSearchChange}
          />
        </div>
        <div className="mb-2 flex items-center justify-end">
          <label className="flex items-center text-small text-default-400">
            Rows per page:
            <select
              className="ml-2 bg-transparent text-small text-default-400 outline-none"
              onChange={onRowsPerPageChange}
              value={rowsPerPage}
            >
              <option value="5">5</option>
              <option value="10">10</option>
              <option value="15">15</option>
              <option value="25">25</option>
              <option value="100">100</option>
            </select>
          </label>
        </div>
      </div>

      <Table
        aria-label="Student Details"
        className="border-none shadow-none"
        selectionMode={tabledatas.checkboxcheck ? "multiple" : undefined}
        selectedKeys={selectedKeys}
        onSelectionChange={handleSelectionChange}
        removeWrapper
      >
        <TableHeader>
          {tabledatas.tableheaders.map((header: any) => {
            const columnHeaderFilter =
              activeHeaderFilter?.key === header.key
                ? activeHeaderFilter
                : null;

            return (
              <TableColumn key={header.key} className="cursor-pointer">
                <div className="flex min-w-[140px] items-center gap-2">
                  <button
                    type="button"
                    className="inline-flex items-center gap-1 text-left"
                    onClick={() => handleSort(header.key)}
                  >
                    {header.label}
                    {sortKey === header.key &&
                      (sortOrder === "asc" ? (
                        <FaArrowUpLong className="text-xs" />
                      ) : (
                        <FaArrowDownLong className="text-xs" />
                      ))}
                  </button>
                  {columnHeaderFilter && (
                    <div onClick={event => event.stopPropagation()}>
                      <Popover
                        placement="bottom-start"
                        showArrow
                        offset={10}
                        isOpen={isHeaderFilterOpen}
                        onOpenChange={open => {
                          setIsHeaderFilterOpen(open);
                          if (!open) {
                            setHeaderFilterQuery("");
                          }
                        }}
                      >
                        <PopoverTrigger>
                          <Button
                            isIconOnly
                            size="sm"
                            variant={
                              columnHeaderFilter.value.length !==
                              columnHeaderFilter.options.length
                                ? "solid"
                                : "light"
                            }
                            className="h-7 min-h-7 w-7 min-w-7 flex-none p-0"
                          >
                            <Image
                              className="h-4 w-4 rounded-none"
                              alt="filter"
                              src="/icons/filter.svg"
                            />
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-[320px] p-0">
                          <div className="flex w-full flex-col gap-4 p-4">
                            <div className="flex items-center justify-between">
                              <span className="text-sm font-medium text-[#131313]">
                                {header.label}
                              </span>
                              <div className="flex gap-2">
                                <Button
                                  size="sm"
                                  className="border border-gray-300 bg-[#fafafb] text-gray-700 hover:bg-gray-200"
                                  onPress={() =>
                                    columnHeaderFilter.onChange(
                                      columnHeaderFilter.options
                                    )
                                  }
                                >
                                  Select All
                                </Button>
                                <Button
                                  size="sm"
                                  className="border border-gray-300 bg-[#fafafb] text-gray-700 hover:bg-gray-200"
                                  onPress={() => {
                                    columnHeaderFilter.onChange([]);
                                    setTimeout(() => {
                                      headerFilterSearchRef.current?.focus();
                                    }, 0);
                                  }}
                                >
                                  Deselect All
                                </Button>
                              </div>
                            </div>
                            <div className="flex items-center gap-2 rounded-2xl border-2 border-[#d4d4d8] bg-white px-3 py-2">
                              <SearchIcon />
                              <input
                                ref={headerFilterSearchRef}
                                type="text"
                                value={headerFilterQuery}
                                autoComplete="off"
                                placeholder={`Search ${header.label.toLowerCase()}...`}
                                className="w-full bg-transparent text-sm text-black outline-none"
                                onChange={e =>
                                  setHeaderFilterQuery(e.target.value || "")
                                }
                              />
                              {headerFilterQuery ? (
                                <button
                                  type="button"
                                  className="text-xs text-gray-500 hover:text-gray-700"
                                  onClick={() => setHeaderFilterQuery("")}
                                >
                                  Clear
                                </button>
                              ) : null}
                            </div>
                            <div className="flex max-h-[240px] w-full flex-col gap-1 overflow-y-auto rounded-2xl border border-gray-300 p-2">
                              {filteredHeaderFilterOptions.map(option => {
                                const isSelected =
                                  columnHeaderFilter.value.includes(
                                    option.value
                                  );
                                const selectedOptions =
                                  columnHeaderFilter.options.filter(item =>
                                    columnHeaderFilter.value.includes(
                                      item.value
                                    )
                                  );

                                return (
                                  <button
                                    key={option.value}
                                    type="button"
                                    className="flex w-full items-center gap-2 rounded-md px-2 py-2 text-left text-xs text-[#111827] hover:bg-gray-100"
                                    onClick={() => {
                                      const nextValues = isSelected
                                        ? selectedOptions.filter(
                                            item => item.value !== option.value
                                          )
                                        : [...selectedOptions, option];

                                      columnHeaderFilter.onChange(nextValues);
                                    }}
                                  >
                                    <span
                                      className={`flex h-4 w-4 items-center justify-center rounded border ${
                                        isSelected
                                          ? "border-yellow-500 bg-yellow-400 text-white"
                                          : "border-gray-300 bg-white text-transparent"
                                      }`}
                                    >
                                      ✓
                                    </span>
                                    <span>{option.label}</span>
                                  </button>
                                );
                              })}
                              {filteredHeaderFilterOptions.length === 0 && (
                                <div className="px-2 py-3 text-xs text-gray-500">
                                  No results found
                                </div>
                              )}
                            </div>
                            <div className="flex justify-end">
                              <Button
                                size="sm"
                                className="border border-gray-300 bg-[#fafafb] text-gray-700 hover:bg-gray-200"
                                onPress={() => {
                                  setIsHeaderFilterOpen(false);
                                  setHeaderFilterQuery("");
                                }}
                              >
                                Close
                              </Button>
                            </div>
                          </div>
                        </PopoverContent>
                      </Popover>
                    </div>
                  )}
                </div>
              </TableColumn>
            );
          })}
        </TableHeader>

        <TableBody items={currentRows} emptyContent="No data available">
          {(row: any) => (
            <TableRow key={row.userId}>
              {tabledatas.tableheaders.map((headerkey: any) => (
                <TableCell key={headerkey.key}>
                  {row[headerkey.key] ?? "N/A"}
                </TableCell>
              ))}
            </TableRow>
          )}
        </TableBody>
      </Table>

      {totalPages > 0 && (
        <div className="flex items-center justify-between px-2 py-2">
          <Pagination
            showControls
            classNames={{cursor: "bg-foreground text-background"}}
            color="default"
            page={page}
            total={totalPages}
            variant="light"
            onChange={setPage}
          />
        </div>
      )}
    </div>
  );
}
